import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import ClickableStatCard from "@/components/ClickableStatCard";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { Plus, Users, Send, MailOpen, MousePointerClick, AlertTriangle, Layers } from "lucide-react";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch {
      /* polling errors are non-fatal */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats(false);
  }, [loadStats]);

  useEffect(() => {
    const timer = setInterval(() => loadStats(true), 15000);
    return () => clearInterval(timer);
  }, [loadStats]);

  const items = stats ? [
    { label: "Subscribers", value: stats.subscribers.toLocaleString(), suffix: "", Icon: Users, to: "/audiences", accent: "var(--brand-primary)" },
    { label: "Segments", value: stats.segments ?? 0, suffix: "", Icon: Layers, to: "/segments", accent: "var(--brand-primary)" },
    { label: "Campaigns sent", value: stats.sent_campaigns, suffix: "", Icon: Send, to: "/campaigns", accent: "var(--accent-green)" },
    { label: "Total opens", value: (stats.total_opens ?? 0).toLocaleString(), suffix: "", Icon: MailOpen, to: "/campaigns", accent: "var(--brand-primary)" },
    { label: "Total clicks", value: (stats.total_clicks ?? 0).toLocaleString(), suffix: "", Icon: MousePointerClick, to: "/campaigns", accent: "var(--accent-red)" },
    { label: "Bounces", value: (stats.total_bounces ?? 0).toLocaleString(), suffix: "", Icon: AlertTriangle, to: "/suppressions?tab=bounce", accent: "#E0000F" },
    { label: "Open rate", value: stats.open_rate, suffix: "%", Icon: MailOpen, to: "/campaigns", accent: "var(--brand-primary)" },
    { label: "Click rate", value: stats.click_rate, suffix: "%", Icon: MousePointerClick, to: "/campaigns", accent: "var(--accent-red)" },
    { label: "Bounce rate", value: stats.bounce_rate ?? 0, suffix: "%", Icon: AlertTriangle, to: "/suppressions?tab=bounce", accent: "#E0000F" },
  ] : [];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Your audience, campaigns and performance at a glance."
        action={
          <Link to="/campaigns/new">
            <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="create-campaign-button">
              <Plus className="h-4 w-4 mr-2" /> New campaign
            </Button>
          </Link>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {items.map((it) => (
                <ClickableStatCard
                  key={it.label}
                  label={it.label}
                  value={it.value}
                  suffix={it.suffix}
                  Icon={it.Icon}
                  to={it.to}
                  accent={it.accent}
                  testId={`stat-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                />
              ))}
            </div>

            <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="label-overline mb-2">Performance</p>
                  <h3 className="font-display font-bold text-2xl tracking-tight">Recent campaigns</h3>
                </div>
                <Link to="/campaigns" className="text-sm font-semibold text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 underline underline-offset-4">
                  View all campaigns
                </Link>
              </div>
              {stats?.chart?.length ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#737373" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#737373" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{ border: "1px solid #E5E7EB", borderRadius: "2px", fontFamily: "Karla", fontSize: 12 }}
                        cursor={{ fill: "rgba(0,47,167,0.05)" }}
                      />
                      <Legend wrapperStyle={{ fontFamily: "Karla", fontSize: 12 }} />
                      <Bar dataKey="opens" name="Opens" fill="#002FA7" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="clicks" name="Clicks" fill="#E0000F" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="bounces" name="Bounces" fill="#737373" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-12 text-sm text-neutral-500 dark:text-neutral-400">
                  No campaigns sent yet. Once you send a campaign, opens, clicks and bounces will appear here.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
